import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, X } from "lucide-react";

// Cookie consent banner.
//
// Behaviour (UK PECR / ICO guidance):
//   1. Banner appears on first visit OR until the user makes a choice.
//   2. "Reject all" is as prominent as "Accept all" and the same single
//      click — never buried behind "Manage".
//   3. We DO NOT set or load any non-essential cookies / scripts before
//      consent. Essential-only storage (theme, history, admin token) is
//      strictly first-party functional and is documented in the policy.
//   4. Choice is persisted in localStorage and the banner stays hidden.
//      A footer button dispatches ``sbi:open-cookie-prefs`` to re-open it.
const KEY = "sbi_cookie_consent";

const loadConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null");
  } catch {
    return null;
  }
};

const saveConsent = (c) => {
  localStorage.setItem(KEY, JSON.stringify({ ...c, ts: Date.now() }));
};

export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("banner"); // banner | manage
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const c = loadConsent();
    if (!c) setOpen(true);
    const reopen = () => {
      const existing = loadConsent();
      setPrefs({
        analytics: existing?.analytics || false,
        marketing: existing?.marketing || false,
      });
      setMode("manage");
      setOpen(true);
    };
    window.addEventListener("sbi:open-cookie-prefs", reopen);
    return () => window.removeEventListener("sbi:open-cookie-prefs", reopen);
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true, accepted: true });
    setOpen(false);
  };
  const rejectAll = () => {
    saveConsent({ essential: true, analytics: false, marketing: false, accepted: false });
    setOpen(false);
  };
  const savePrefs = () => {
    saveConsent({ essential: true, ...prefs, accepted: prefs.analytics || prefs.marketing });
    setOpen(false);
  };

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[100] bg-card border border-border rounded-2xl shadow-2xl animate-float-up"
      role="dialog"
      aria-label="Cookie preferences"
    >
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-base text-foreground">
              We respect your privacy
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              We use only the cookies and local storage we genuinely need to
              run the site. Optional analytics help us improve the
              recommendations &mdash; you can decide.{" "}
              <Link to="/cookies" className="underline hover:text-foreground">
                Read the Cookie Policy
              </Link>
              .
            </p>
          </div>
          <button
            onClick={rejectAll}
            aria-label="Close and reject non-essential cookies"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {mode === "manage" && (
          <div className="space-y-2.5 mb-4">
            <label className="flex items-start gap-3 text-xs p-2.5 rounded-lg bg-secondary/40 cursor-not-allowed opacity-90">
              <input type="checkbox" checked readOnly className="mt-0.5 accent-emerald-500" />
              <div>
                <div className="font-semibold text-foreground">Essential (always on)</div>
                <div className="text-muted-foreground">Required to run the site — theme, recent searches, admin sign-in.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 text-xs p-2.5 rounded-lg hover:bg-secondary/40 transition cursor-pointer">
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
                data-testid="cookie-pref-analytics"
                className="mt-0.5 accent-emerald-500"
              />
              <div>
                <div className="font-semibold text-foreground">Analytics</div>
                <div className="text-muted-foreground">Aggregated, anonymous usage data so we can improve recommendations.</div>
              </div>
            </label>
            <label className="flex items-start gap-3 text-xs p-2.5 rounded-lg hover:bg-secondary/40 transition cursor-pointer">
              <input
                type="checkbox"
                checked={prefs.marketing}
                onChange={(e) => setPrefs((p) => ({ ...p, marketing: e.target.checked }))}
                data-testid="cookie-pref-marketing"
                className="mt-0.5 accent-emerald-500"
              />
              <div>
                <div className="font-semibold text-foreground">Affiliate / marketing</div>
                <div className="text-muted-foreground">Helps the retailer attribute purchases. Set when you click an Amazon link.</div>
              </div>
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-end">
          {mode === "banner" ? (
            <>
              <button
                onClick={() => setMode("manage")}
                data-testid="cookie-manage-btn"
                className="px-3 py-2 text-xs rounded-lg border border-border text-foreground/80 hover:bg-secondary transition"
              >
                Manage preferences
              </button>
              <button
                onClick={rejectAll}
                data-testid="cookie-reject-btn"
                className="px-3 py-2 text-xs rounded-lg border border-border text-foreground/80 hover:bg-secondary transition"
              >
                Reject all
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-accept-btn"
                className="px-3 py-2 text-xs rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition"
              >
                Accept all
              </button>
            </>
          ) : (
            <>
              <button
                onClick={rejectAll}
                data-testid="cookie-reject-btn"
                className="px-3 py-2 text-xs rounded-lg border border-border text-foreground/80 hover:bg-secondary transition"
              >
                Reject all
              </button>
              <button
                onClick={savePrefs}
                data-testid="cookie-save-prefs"
                className="px-3 py-2 text-xs rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition"
              >
                Save preferences
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Read-only helper for other modules to check whether analytics consent
// has been granted. Returns ``false`` until the user explicitly opts in.
export const hasAnalyticsConsent = () => {
  const c = loadConsent();
  return Boolean(c && c.analytics);
};

export default CookieConsent;
